import {
  RESIZE_HANDLES,
  boardToScreen,
  handlePoint,
  rectCenter,
  vDist,
  type Rect,
  type ResizeHandle,
  type Vec,
  type Viewport,
} from '@kanvas/board-core';

export type HandleId = ResizeHandle | 'rotate';

/** Selection box plus its rotation, shared by the overlay and the gestures. */
export interface SelectionFrame {
  bounds: Rect;
  rotation: number;
}

export const HANDLE_SIZE = 9;
export const HANDLE_HIT_SLOP = 6;
export const ROTATE_OFFSET = 24;
/** Below this screen size the side handles are dropped, only corners remain. */
export const MIN_SIZE_FOR_EDGE_HANDLES = 46;
/** Below this the whole handle set is hidden and only the outline is drawn. */
export const MIN_SIZE_FOR_HANDLES = 16;

export interface HandleSpec {
  id: HandleId;
  /** Screen position in CSS pixels. */
  point: Vec;
  /** CSS cursor to show, already rotated to match the selection. */
  cursor: string;
}

const BASE_CURSOR_ANGLE: Record<ResizeHandle, number> = {
  n: 0,
  ne: 45,
  e: 90,
  se: 135,
  s: 180,
  sw: 225,
  w: 270,
  nw: 315,
};

const CURSOR_BY_OCTANT = [
  'ns-resize',
  'nesw-resize',
  'ew-resize',
  'nwse-resize',
  'ns-resize',
  'nesw-resize',
  'ew-resize',
  'nwse-resize',
];

/** Resize cursor for a handle on a rotated selection. */
export function resizeCursor(handle: ResizeHandle, rotation: number): string {
  const deg = (BASE_CURSOR_ANGLE[handle] + (rotation * 180) / Math.PI + 360 * 4) % 360;
  const octant = Math.round(deg / 45) % 8;
  return CURSOR_BY_OCTANT[octant]!;
}

export function selectionScreenSize(frame: SelectionFrame, vp: Viewport): Vec {
  return { x: frame.bounds.w * vp.zoom, y: frame.bounds.h * vp.zoom };
}

export function handleSpecs(
  frame: SelectionFrame,
  vp: Viewport,
  opts: { resize?: boolean } = {},
): HandleSpec[] {
  const size = selectionScreenSize(frame, vp);
  if (Math.max(size.x, size.y) < MIN_SIZE_FOR_HANDLES) return [];

  const out: HandleSpec[] = [];

  if (opts.resize !== false) {
    const compact = Math.min(size.x, size.y) < MIN_SIZE_FOR_EDGE_HANDLES;
    const wanted = compact
      ? (['nw', 'ne', 'se', 'sw'] as ResizeHandle[])
      : RESIZE_HANDLES;

    for (const id of wanted) {
      out.push({
        id,
        point: boardToScreen(vp, handlePoint(frame.bounds, id, frame.rotation)),
        cursor: resizeCursor(id, frame.rotation),
      });
    }
  }

  out.push({ id: 'rotate', point: rotateHandlePoint(frame, vp), cursor: 'grab' });
  return out;
}

/** The rotate grip floats above the top edge, in screen space. */
export function rotateHandlePoint(frame: SelectionFrame, vp: Viewport): Vec {
  const topCenterBoard = handlePoint(frame.bounds, 'n', frame.rotation);
  const centerBoard = rectCenter(frame.bounds);
  const top = boardToScreen(vp, topCenterBoard);
  const center = boardToScreen(vp, centerBoard);
  const dx = top.x - center.x;
  const dy = top.y - center.y;
  const len = Math.hypot(dx, dy) || 1;
  return { x: top.x + (dx / len) * ROTATE_OFFSET, y: top.y + (dy / len) * ROTATE_OFFSET };
}

export function hitHandle(
  frame: SelectionFrame,
  vp: Viewport,
  screenPoint: Vec,
  opts: { resize?: boolean } = {},
): HandleSpec | null {
  const specs = handleSpecs(frame, vp, opts);
  const reach = HANDLE_SIZE / 2 + HANDLE_HIT_SLOP;
  let best: { spec: HandleSpec; d: number } | null = null;
  for (const spec of specs) {
    const d = vDist(spec.point, screenPoint);
    if (d > reach) continue;
    if (!best || d < best.d) best = { spec, d };
  }
  return best?.spec ?? null;
}

/** Corners of the selection frame in screen space, for drawing the outline. */
export function frameScreenCorners(frame: SelectionFrame, vp: Viewport): Vec[] {
  return (['nw', 'ne', 'se', 'sw'] as ResizeHandle[]).map((h) =>
    boardToScreen(vp, handlePoint(frame.bounds, h, frame.rotation)),
  );
}
